import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

import { BaseView } from '@/components/base'
import { Wallet } from '../Wallet'
import { HomePageSliders } from '../HomePageSliders'
import { SavingsList } from '../SavingsList'
import { CurrenciesList } from '../CurrenciesList'
import { StocksList } from '../StocksList'

import { useGetWallet, useGetMe, useKemCardScreenState } from '@/domain'
import { useUserStore, useAppPreferencesStore } from '@/store'
import { QUERY_KEYS } from '@/constants/queryKeys'

interface DashboardContentRef {
  handleRefreshBalance: () => Promise<void>
}

export const DashboardContent = React.forwardRef<DashboardContentRef>(
  (_, ref) => {
    const queryClient = useQueryClient()
    // Включаем API запросы для получения актуального баланса
    const {
      data: walletData,
      isLoading: isWalletLoading,
      isError: hasWalletError,
      refetch: refetchWallet,
    } = useGetWallet()
    const { isLoading: isUserLoading } = useGetMe()

    // Initialize KemCard screen state determination on app load
    // This will determine which card screen to show and cache it in the store
    const { refresh: refreshKemCardState } = useKemCardScreenState()

    const userAuthenticated = useUserStore((state) => state.userAuthenticated)
    const setWallet = useUserStore((state) => state.setWallet)
    const { displayCurrency } = useAppPreferencesStore()

    // Fallback данные если API недоступен или нет аутентификации
    const fallbackWalletData = {
      walletId: '',
      totalBalance: 0,
      displayCurrency: 'USD',
      completeCurrencies: [],
      cards: [],
    }

    // Используем реальные данные если доступны, иначе fallback
    const activeWalletData = React.useMemo(
      () => walletData || fallbackWalletData,
      [walletData],
    )

    // Update wallet in store when data changes
    // NOTE: Removed problematic sync logic that overrode server currency with local storage
    // The useLoadDisplayCurrency hook now handles proper server-to-local synchronization
    React.useEffect(() => {
      if (walletData && walletData.walletId) {
        setWallet(walletData)
      }
    }, [walletData, setWallet])

    const isLoading = isWalletLoading || isUserLoading
    const hasError = hasWalletError

    // Функция для принудительного обновления баланса
    const handleRefreshBalance = React.useCallback(async () => {
      try {
        await refetchWallet()
        // Также обновляем другие связанные данные
        await queryClient.refetchQueries({ queryKey: [QUERY_KEYS.WALLET] })
        // Refresh KemCard screen state as well
        await refreshKemCardState()
      } catch (error) {
        toast.error('Failed to refresh')
      }
    }, [refetchWallet, queryClient, refreshKemCardState])

    // Обработка ошибок загрузки
    React.useEffect(() => {
      if (hasWalletError) {
        toast.error('Failed to load wallet data')
      }
    }, [hasWalletError])

    // Expose refresh function via ref
    React.useImperativeHandle(
      ref,
      () => ({
        handleRefreshBalance,
      }),
      [handleRefreshBalance],
    )

    return (
      <BaseView gap={22}>
        {/* Wallet section */}
        <Wallet
          balance={activeWalletData?.totalBalance || 0}
          currencyCode={
            displayCurrency?.isin || activeWalletData?.displayCurrency || 'USD'
          }
          skeletonActive={isLoading}
          containerStyle={{ margin: '0' }}
        />

        {/* Home sliders section */}
        <HomePageSliders
          skeletonActive={isLoading}
          userAuthenticated={userAuthenticated}
        />

        {/* Currencies list section */}
        <CurrenciesList
          skeletonActive={isLoading}
          hasErrorOnLoadingCurrencies={hasError}
          currencyCode={
            displayCurrency?.isin || activeWalletData?.displayCurrency || 'USD'
          }
          currencies={activeWalletData?.completeCurrencies || []}
        />

        {/* Savings section */}
        <SavingsList isLoading={isLoading} />

        {/* Stocks section */}
        <StocksList isLoading={isLoading} />
      </BaseView>
    )
  },
)

DashboardContent.displayName = 'DashboardContent'
